"use client";

/**
 * A full page for a home section that had none (Gordon 2026-10-31: "clicking
 * a home header section … should open a page for each section with
 * everything on it"): Videos, Labels, Playlists, Demos & Beats, People.
 * Same data as the home rows, without the row caps — search, sort and the
 * 1–4-per-row picker.
 */

import { useEffect, useMemo, useState, type ElementType } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Building, ChevronDown, Disc, FileVideo, ListMusic, Search, UserRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { PerRowPicker, PER_ROW_GRID_PAGE, usePerRow } from "@/components/ui/per-row-picker";
import { getDemoContent, getFeaturedLabels, getPopularPlaylists, getPublicUsers } from "@/services/home";
import { getLatestVideos } from "@/services/videos";
import { getThumbnailUrl } from "@/lib/youtube";
import { cn } from "@/lib/utils";
import { GenerativeArtistAvatar } from "@/components/media/generative-artist-avatar";
import { getCoverIndex } from "@/services/cover-index";
import { realPhoto } from "@/lib/placeholder-photo";
import { useUrlParams } from "@/hooks/use-url-params";
import { CrateToggle, useCrateMode } from "@/components/crates/crate-toggle";
import { CrateView } from "@/components/crates/crate-view";
import { roleFamiliesOf } from "@/lib/people-roles";

export type SectionKind = "videos" | "labels" | "playlists" | "demos" | "people";

interface Card { id: string; title: string; subtitle?: string; imageUrl?: string; href: string; date?: string; /** extra words the search matches */ search?: string }

// Roles that say nothing about what someone does — not shown under a name.
const GENERIC_ROLES = new Set(["creator", "consumer", "business consumer", "other", "super admin", "admin", "print on demand"]);

const ICON: Record<SectionKind, ElementType> = {
  videos: FileVideo, labels: Building, playlists: ListMusic, demos: Disc, people: UserRound,
};

const iso = (v: any): string | undefined =>
  typeof v === "string" ? v : v?.toDate?.()?.toISOString?.() ?? (typeof v?.seconds === "number" ? new Date(v.seconds * 1000).toISOString() : undefined);

async function loadCards(kind: SectionKind): Promise<Card[]> {
  switch (kind) {
    case "videos":
      return (await getLatestVideos(300)).map(v => {
        // Bar-free 16:9 YouTube frame, same as the home row.
        let thumb = v.thumbnailUrl || (v.youtubeId ? getThumbnailUrl(v.youtubeId, "mqdefault") : "");
        if (/(?:img\.youtube\.com|i\.ytimg\.com)\/vi\//.test(thumb)) thumb = thumb.replace(/\/(?:maxresdefault|sddefault|hqdefault|default|mqdefault)\.jpg/, "/mqdefault.jpg");
        return { id: v.videoId, title: v.title || "", subtitle: v.artist?.name || "", imageUrl: thumb, href: `/videos/${v.videoId}`, date: iso(v.createdAt) };
      });
    case "labels":
      return (await getFeaturedLabels(500)).map(l => ({ id: l.id, title: l.name, subtitle: l.genre, imageUrl: l.imageUrl, href: l.profileLink || `/labels/${l.id}`, date: l.dateAdded }));
    case "playlists":
      // The service fills creator/dateAdded beyond the Playlist type.
      return (await getPopularPlaylists(300)).map((p: any) => ({ id: p.id, title: p.title, subtitle: p.creator, imageUrl: p.imageUrl, href: `/playlist/${p.id}`, date: p.dateAdded }));
    case "demos": {
      const [list, covers] = await Promise.all([getDemoContent(), getCoverIndex().catch(() => null)]);
      return list.map((d: any) => ({ id: d.id, title: d.title, subtitle: d.artist, imageUrl: d.imageUrl || covers?.forWork(d) || "", href: `/music/${d.id}`, date: d.dateAdded }));
    }
    case "people":
      // What each person does under their name, and searchable by it — coπ's
      // "show guitarists" opens /people?q=guitar (Gordon 2026-11-02).
      return (await getPublicUsers(300)).map((u: any) => {
        const roles: string[] = (Array.isArray(u.roles) ? u.roles : []).filter((r: unknown): r is string => typeof r === "string" && !GENERIC_ROLES.has(r.toLowerCase()));
        const instruments: string[] = (Array.isArray(u.instruments) ? u.instruments : []).filter((r: unknown): r is string => typeof r === "string");
        return {
          id: u.id, title: u.displayName || "", subtitle: roles.slice(0, 3).join(" · ") || undefined,
          imageUrl: realPhoto(u.profileImageUrl), href: `/profile/${u.id}`, date: iso(u.createdAt),
          search: [...roles, ...instruments, ...roleFamiliesOf({ roles: u.roles, instruments: u.instruments })].join(" "),
        };
      });
  }
}

type Sort = "newest" | "az" | "za";
const PAGE = 60;

export function SectionPage({ kind }: { kind: SectionKind }) {
  const t = useTranslations("browse");
  const tNav = useTranslations("nav");
  const tHome = useTranslations("home");
  const title = kind === "demos" ? tHome("demos") : kind === "people" ? tHome("people") : tNav(kind);
  const Icon = ICON[kind];
  const [cards, setCards] = useState<Card[] | null>(null);
  const [crate, setCrate] = useCrateMode(`section-${kind}`);
  const [failed, setFailed] = useState(false);
  const [q, setQ] = useState("");
  // ?q=guitar — the search box starts filled (coπ's "show guitarists").
  useUrlParams((sp) => { const v = sp.get("q"); if (v) setQ(v); });
  const [sort, setSort] = useState<Sort>("newest");
  const [shown, setShown] = useState(PAGE);
  const [perRow, setPerRow] = usePerRow("bb-section-page-per-row");

  useEffect(() => {
    let alive = true;
    loadCards(kind)
      .then(list => { if (alive) setCards(list.filter(c => c.title)); })
      .catch(() => { if (alive) { setCards([]); setFailed(true); } });
    return () => { alive = false; };
  }, [kind]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = (cards ?? []).filter(c => !needle || `${c.title} ${c.subtitle ?? ""} ${c.search ?? ""}`.toLowerCase().includes(needle));
    if (sort === "az") list.sort((a, b) => a.title.localeCompare(b.title));
    else if (sort === "za") list.sort((a, b) => b.title.localeCompare(a.title));
    else list.sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));
    return list;
  }, [cards, q, sort]);

  useEffect(() => { setShown(PAGE); }, [q, sort]);

  return (
    <div className="px-page pt-3 sm:pt-4 pb-12 space-y-3">
      {/* Same toolbar as Releases and Artists (Andrés 2026-11-01: search sat
          in a different place on each page): title + count, then search on
          the left with the page's controls right after it. */}
      <div className="flex items-center gap-2">
        <Breadcrumbs heading size="lg" items={[{ label: title, icon: Icon }]} className="min-w-0" />
        {cards && <span className="shrink-0 text-xs text-muted-foreground">{visible.length}</span>}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder={t("search", { title })} aria-label={t("search", { title })} className="pl-9 h-9 bg-secondary/50" />
        </div>
        <div className="relative shrink-0">
          <select value={sort} onChange={e => setSort(e.target.value as Sort)} aria-label={t("sort")}
            className="h-9 w-auto appearance-none rounded-md border border-input bg-background ps-2 pe-8 text-sm">
            <option value="newest">{t("sortNewest")}</option>
            <option value="az">{t("sortAZ")}</option>
            <option value="za">{t("sortZA")}</option>
          </select>
          <ChevronDown aria-hidden="true" className="pointer-events-none absolute end-2 top-1/2 h-4 w-4 -translate-y-1/2" />
        </div>
        {!crate && <PerRowPicker value={perRow} onChange={setPerRow} />}
        <CrateToggle crate={crate} onChange={setCrate} />
      </div>

      {cards === null ? (
        <div className={cn("grid gap-3", PER_ROW_GRID_PAGE[perRow])} aria-busy="true">
          {Array.from({ length: 8 }, (_, i) => <div key={i} className="aspect-square animate-pulse bg-muted rounded-[var(--thumbnail-radius,0px)]" />)}
        </div>
      ) : visible.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground">{failed ? t("loadFailed") : q ? t("noMatches") : t("empty")}</p>
      ) : crate ? (
        // The vinyl drawer (Gordon 2026-11-02): the same cards as records to flip through.
        <CrateView items={visible} label={title} className="py-4" />
      ) : (
        <>
          <ul className={cn("grid gap-3", PER_ROW_GRID_PAGE[perRow])}>
            {visible.slice(0, shown).map(c => (
              <li key={c.id} className="min-w-0">
                <Link href={c.href} className="group block">
                  <div className="relative aspect-square overflow-hidden bg-muted rounded-[var(--thumbnail-radius,0px)]">
                    {c.imageUrl ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={c.imageUrl} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    ) : (
                      // No artwork anywhere → the animated vector with the name.
                      <GenerativeArtistAvatar name={c.title} className="h-full w-full" animate />
                    )}
                  </div>
                  <p className="capitalize mt-1.5 truncate text-sm font-medium group-hover:text-primary">{c.title}</p>
                  {c.subtitle && <p className="truncate text-xs text-muted-foreground">{c.subtitle}</p>}
                </Link>
              </li>
            ))}
          </ul>
          {shown < visible.length && (
            <div className="flex justify-center pt-2">
              <Button variant="outline" onClick={() => setShown(n => n + PAGE)}>{t("showMore")}</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
